/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get("cart-drawer-disclosure")) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector("summary");
      this.closeBtn = this.querySelector(".js-close");
      this.overlay = document
        .getElementById("cart-drawer")
        .querySelector(".overlay");
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches(".js-close")) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add("is-visible");
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === "Escape" && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener("click", this.clickHandler);
      this.addEventListener("keyup", this.keyupHandler);
      this.overlay.addEventListener("click", this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove("is-visible");
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener("click", this.clickHandler);
      this.removeEventListener("keyup", this.keyupHandler);
      this.overlay.removeEventListener("click", this.clickHandler);
    }
  }

  customElements.define("cart-drawer-disclosure", CartDrawerDisclosure);
}

// Set up a single event handler for cart refreshes
document.addEventListener('cart:refresh-requested', (event) => {
  // Avoid refresh loops by checking the source
  const source = event.detail?.source || 'unknown';
  console.log(`Refresh requested by: ${source}`);
  
  // Use a timeout to ensure DOM is ready
  setTimeout(() => {
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      cartItems.refresh();
    }
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.refresh();
    }
  }, 300);
});

if (!customElements.get("cart-drawer")) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector(".cart-drawer__summary");
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener(
        "dispatch:cart-drawer:refresh",
        this.refreshHandler
      );
      document.removeEventListener(
        "on:bfcache:load-restore",
        this.refreshHandler
      );
      document.removeEventListener(
        "dispatch:cart-drawer:open",
        this.openDrawerViaEventHandler
      );
      document.removeEventListener(
        "dispatch:cart-drawer:close",
        this.closeDrawerViaEventHandler
      );
      document.removeEventListener(
        "cart:inventory-updated",
        this.inventoryUpdateHandler
      );
      document.removeEventListener(
        "cart:check-inventory",
        this.inventoryCheckHandler
      );

      if (Shopify.designMode) {
        document.removeEventListener(
          "shopify:section:select",
          this.sectionSelectHandler
        );
        document.removeEventListener(
          "shopify:section:deselect",
          this.sectionDeselectHandler
        );
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener(
          "shopify:payment_button:loaded",
          this.dcbLoadedHandler
        );
      }
    }

    init() {
      const cartIcon = document.getElementById("cart-icon");
      if (cartIcon) {
        cartIcon.setAttribute("role", "button");
        cartIcon.setAttribute("aria-haspopup", "dialog");

        cartIcon.addEventListener("click", (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener("keydown", (evt) => {
          if (evt.key !== " ") return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }

      // Add inventory warning message if not already present
      this.ensureInventoryWarning();
    }

    /**
     * Ensures the inventory warning element exists
     */
    ensureInventoryWarning() {
      const inventoryWarning = this.querySelector(
        "#cart-drawer-inventory-warning"
      );
      if (!inventoryWarning) {
        const warningElement = document.createElement("div");
        warningElement.id = "cart-drawer-inventory-warning";
        warningElement.className =
          "inventory-warning bg-error-bg text-error-text p-4 mt-4 mb-4 text-center";
        warningElement.innerHTML =
          "<p>Some items in your cart are out of stock. Please remove them to continue checkout.</p>";
        warningElement.hidden = true;

        // Insert before checkout buttons
        const checkoutButtons = this.querySelector(
          ".cart-drawer__checkout-buttons"
        );
        if (checkoutButtons) {
          checkoutButtons.parentNode.insertBefore(
            warningElement,
            checkoutButtons
          );
        }
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener(
          "shopify:section:select",
          this.sectionSelectHandler
        );
        document.addEventListener(
          "shopify:section:deselect",
          this.sectionDeselectHandler
        );
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener(
        "dispatch:cart-drawer:open",
        this.openDrawerViaEventHandler
      );
      document.addEventListener(
        "dispatch:cart-drawer:close",
        this.closeDrawerViaEventHandler
      );

      if (
        this.cartSummary.classList.contains(
          "cart-drawer--checkout--sticky-true"
        )
      ) {
        this.dcbLoadedHandler =
          this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener(
          "shopify:payment_button:loaded",
          this.dcbLoadedHandler
        );
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener(
        "dispatch:cart-drawer:refresh",
        this.refreshHandler
      );
      document.addEventListener("on:bfcache:load-restore", this.refreshHandler);

      // Listen for inventory updates
      this.inventoryUpdateHandler = this.handleInventoryUpdate.bind(this);
      document.addEventListener(
        "cart:inventory-updated",
        this.inventoryUpdateHandler
      );

      // Listen for direct inventory check requests
      this.inventoryCheckHandler = () => {
        // Fetch an updated cart and check inventory
        fetch("/cart.js")
          .then((response) => response.json())
          .then((cart) => {
            if (cart && cart.items && cart.items.length > 0) {
              // Trigger a refresh which will run inventory checks
              this.refresh();
            }
          })
          .catch((error) => console.error("Error fetching cart:", error));
      };
      document.addEventListener(
        "cart:check-inventory",
        this.inventoryCheckHandler
      );
    }

    /**
     * Handles inventory status updates
     * @param {CustomEvent} event - The inventory update event
     */
    handleInventoryUpdate(event) {
      const { hasOutOfStockItems, hasInsufficientStockItems } = event.detail;

      // Show/hide inventory warning
      const inventoryWarning = this.querySelector(
        "#cart-drawer-inventory-warning"
      );
      if (inventoryWarning) {
        if (hasOutOfStockItems) {
          inventoryWarning.innerHTML =
            "<p>Some items in your cart are out of stock. Please remove them to continue checkout.</p>";
          inventoryWarning.hidden = false;
          inventoryWarning.classList.remove("inventory-warning--insufficient");
        } else if (hasInsufficientStockItems) {
          inventoryWarning.innerHTML =
            "<p>Some items in your cart have insufficient stock. Please adjust quantities to continue.</p>";
          inventoryWarning.hidden = false;
          inventoryWarning.classList.add("inventory-warning--insufficient");
        } else {
          inventoryWarning.hidden = true;
        }
      }

      // Update checkout buttons
      this.updateCheckoutButtonsForInventory(
        hasOutOfStockItems,
        hasInsufficientStockItems
      );

      // Recalculate heights for sticky elements
      if (
        this.cartSummary.classList.contains(
          "cart-drawer--checkout--sticky-true"
        )
      ) {
        CartDrawer.recalculateCssVarHeights();
      }
    }

    /**
     * Updates checkout buttons based on inventory status
     * @param {boolean} hasOutOfStockItems - Whether out-of-stock items are in the cart
     * @param {boolean} hasInsufficientStockItems - Whether items have insufficient stock
     */
    updateCheckoutButtonsForInventory(
      hasOutOfStockItems,
      hasInsufficientStockItems
    ) {
      // Use a more specific selector to target all checkout-related buttons in the drawer
      const checkoutButtons = this.querySelectorAll(
        '.cart-drawer__checkout-buttons button, [name="checkout"], [onclick*="checkout"]'
      );

      checkoutButtons.forEach((button) => {
        if (hasOutOfStockItems || hasInsufficientStockItems) {
          // Disable checkout buttons
          button.disabled = true;
          button.classList.add("opacity-50", "cursor-not-allowed");

          // Store the original onclick if we haven't already
          if (
            !button.hasAttribute("data-original-onclick") &&
            button.getAttribute("onclick")
          ) {
            button.setAttribute(
              "data-original-onclick",
              button.getAttribute("onclick")
            );
          }

          // Override onclick with appropriate messaging
          button.onclick = (e) => {
            e.preventDefault();

            // Scroll to the inventory warning
            const inventoryWarning = this.querySelector(
              "#cart-drawer-inventory-warning"
            );
            if (inventoryWarning) {
              inventoryWarning.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }

            return false;
          };
        } else {
          // Only enable if MOV requirements are met
          if (window.cartMeetsMovRequirement === false) return;

          // Enable checkout buttons
          button.disabled = false;
          button.classList.remove("opacity-50", "cursor-not-allowed");

          // Restore the original onclick functionality
          const originalOnClick = button.getAttribute("data-original-onclick");
          if (originalOnClick) {
            button.setAttribute("onclick", originalOnClick);
            button.removeAttribute("data-original-onclick");
            button.onclick = null;
          }
        }
      });
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest(".shopify-section")) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest(".shopify-section")) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector(
        'quick-add-drawer[aria-hidden="false"]'
      );
      const overlay = document.querySelector(".js-overlay.is-visible");

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = "200ms";
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(
        () => {
          super.open(opener, elementToFocus, () => {
            if (
              this.cartSummary.classList.contains(
                "cart-drawer--checkout--sticky-true"
              )
            ) {
              CartDrawer.recalculateCssVarHeights();
            }
          });

          if (
            this.cartSummary.classList.contains(
              "cart-drawer--checkout--sticky-true"
            )
          ) {
            CartDrawer.recalculateCssVarHeights();
          }

          if (overlay) overlay.style.transitionDelay = "";
        },
        quickAddDrawer ? 200 : 0
      );
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector("cart-items");
        // Always do a full refresh when called explicitly to ensure everything updates
        const response = this.getSectionsToRender().map(
          (section) => section.section
        );
        const cartResponse = await fetch(`?sections=${response.join(",")}`);
        const sections = await cartResponse.json();

        // If it's a drawer, we need to fully render contents
        await this.renderContents({ sections }, false);

        // If we also have cartItems, make sure it's refreshed internally too
        if (cartItems) {
          cartItems.refresh();
        }

        // Make sure to recalculate heights for sticky elements
        CartDrawer.recalculateCssVarHeights();
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: {
              error: this.errorMsg?.textContent,
            },
          })
        );
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (
          !document.querySelector(`script[src="${theme.scripts.cartItems}"]`)
        ) {
          await loadScript(theme.scripts.cartItems);
        }

        if (
          !document.querySelector(
            `script[src="${theme.scripts.shippingCalculator}"]`
          ) &&
          this.dataset.shippingCalculator
        ) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      // Listen for the MOV update event to recalculate heights if needed
      document.addEventListener("cart:mov-updated", (event) => {
        if (
          this.cartSummary.classList.contains(
            "cart-drawer--checkout--sticky-true"
          )
        ) {
          CartDrawer.recalculateCssVarHeights();
        }
      });

      // Listen for inventory updates
      document.addEventListener("cart:inventory-updated", (event) => {
        if (
          this.cartSummary.classList.contains(
            "cart-drawer--checkout--sticky-true"
          )
        ) {
          CartDrawer.recalculateCssVarHeights();
        }
      });

      if (openDrawer && this.getAttribute("open") === null) {
        setTimeout(() => this.open());
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: "cart-drawer",
          section: this.closest(".shopify-section").id.replace(
            "shopify-section-",
            ""
          ),
          selector: "cart-drawer",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
        {
          id: "customer-tags-section",
          section: "customer-tags-section",
          selector: ".customer-tags-container",
        },
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent("on:css-var-height:update"));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement("template");
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define("cart-drawer", CartDrawer);
}

/**
 * Enhanced Shipping Calculator with improved error handling
 * Handles Minimum Order Value (MOV) errors gracefully
 */

class ShippingCalculator {
  constructor(element) {
    this.element = element;
    this.form = this.element.querySelector(".shipping-calculator__form");
    this.countrySelect = this.element.querySelector("#address_country");
    this.provinceSelect = this.element.querySelector("#address_province");
    this.postalField = this.element.querySelector("#address_zip");
    this.submitButton = this.element.querySelector('button[type="submit"]');
    this.responseElement = this.element.querySelector(
      ".shipping-calculator__response"
    );
    this.errorMessageMapping = {
      OUT_OF_DELIVERY_AREA: "We do not ship to this destination.",
      MINIMUM_ORDER_NOT_MET:
        "Your order total does not meet the minimum order value for your region.",
      NO_SHIPPING_OPTION:
        "No suitable shipping options available for this order.",
      CALCULATION_ERROR: "An error occurred while calculating shipping rates.",
      INVENTORY_ISSUES:
        "Some items in your cart are out of stock. Please remove them to calculate shipping.",
      LOGIN_REQUIRED: "Please create an account to view shipping cost estimates."
    };
  
    // Check if customer is logged in
    this.isLoggedIn = this.checkCustomerLoginStatus();
  
    // Add listener for the MOV update event
    document.addEventListener(
      "cart:mov-updated",
      this.handleMovUpdate.bind(this)
    );
  
    // Add listener for inventory status updates
    document.addEventListener(
      "cart:inventory-updated",
      this.handleInventoryUpdate.bind(this)
    );
  
    this.init();
  }
  
  /**
   * Check if user is logged in
   */
  checkCustomerLoginStatus() {
    const customerIdMeta = document.querySelector('meta[name="customer-id"]');
    return customerIdMeta && customerIdMeta.content && customerIdMeta.content !== '0';
  }

  // Add method to handle inventory updates
  handleInventoryUpdate(event) {
    const { hasOutOfStockItems } = event.detail;

    if (hasOutOfStockItems) {
      // If there's an active calculation showing, update it based on inventory status
      if (!this.responseElement.hidden) {
        this.showErrorMessage(this.errorMessageMapping["INVENTORY_ISSUES"]);
      }

      // Disable the submit button if there are out-of-stock items
      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add("opacity-50", "cursor-not-allowed");
      }
    } else {
      // Re-enable the submit button if there are no out-of-stock items
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove("opacity-50", "cursor-not-allowed");
      }
    }
  }

  // Add method to handle MOV updates
  handleMovUpdate(event) {
    const {
      meetsMOV,
      requiredMOV,
      cartTotal,
      amountRemaining,
      customerRegion,
      isSpecialCustomer,
    } = event.detail;

    // If there's an active calculation showing, update it based on new MOV status
    if (
      !this.responseElement.hidden &&
      this.responseElement.querySelector(".shipping-calculator__mov-info")
    ) {
      if (!meetsMOV) {
        const amountFormatted = this.formatMoney(amountRemaining);
        let message = `Add ${amountFormatted} more to your cart to qualify for shipping.`;

        if (isSpecialCustomer) {
          message = `As a special customer, add ${amountFormatted} more to your cart to qualify for shipping.`;
        }

        const movInfo = this.responseElement.querySelector(
          ".shipping-calculator__mov-info"
        );
        if (movInfo) movInfo.textContent = message;
      } else {
        // If MOV is now met and no inventory issues, re-run the calculation
        if (!window.cartHasOutOfStockItems) {
          this.form.dispatchEvent(new Event("submit"));
        }
      }
    }
  }

  init() {
    if (this.countrySelect && this.provinceSelect) {
      new Shopify.CountryProvinceSelector(
        this.countrySelect.id,
        this.provinceSelect.id,
        { hideElement: "address_province_container" }
      );
    }

    this.bindEvents();

    // Check inventory status initially
    this.checkInventoryStatus();
  }

  checkInventoryStatus() {
    // Check if we already know about inventory status from CartItems component
    if (typeof window.cartHasOutOfStockItems !== "undefined") {
      this.handleInventoryUpdate({
        detail: { hasOutOfStockItems: window.cartHasOutOfStockItems },
      });
    } else {
      // Otherwise check DOM for out-of-stock items
      const outOfStockItems = document.querySelectorAll(
        ".cart-item--out-of-stock"
      );
      const hasOutOfStockItems = outOfStockItems.length > 0;
      this.handleInventoryUpdate({ detail: { hasOutOfStockItems } });
    }
  }

  bindEvents() {
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
  }

  /**
   * Handles form submission.
   * @param {Event} evt - Event object.
   */
  handleSubmit(evt) {
    evt.preventDefault();

    // Check for out-of-stock items first
    if (window.cartHasOutOfStockItems) {
      this.showErrorMessage(this.errorMessageMapping["INVENTORY_ISSUES"]);
      return;
    }
  
    // Get cart total
    const cartTotalElement = document.querySelector('meta[name="cart-total"]');
    const cartTotal = cartTotalElement ? parseFloat(cartTotalElement.content) : 0;
    const generalMOV = 15000; // £150 in pennies
  
    // Check login status for non-logged in users with sufficient cart total
    if (!this.isLoggedIn && cartTotal >= generalMOV) {
      this.showErrorMessage(this.errorMessageMapping["LOGIN_REQUIRED"]);
      return;
    }
  
    this.submitButton.classList.add("btn--loading");
    this.hideErrorMessage();

    const formData = new FormData(this.form);
    const data = {
      shipping_address: {
        country: formData.get("address[country]"),
        province: formData.get("address[province]"),
        zip: formData.get("address[zip]"),
      },
    };

    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    };

    fetch(`${routes.cart_url}/shipping_rates.json`, options)
      .then((response) => response.json())
      .then((response) => {
        this.handleRatesResponse(response);
      })
      .catch((error) => {
        console.error(error);
        this.showErrorMessage("An error occurred. Please try again later.");
      })
      .finally(() => {
        this.submitButton.classList.remove("btn--loading");
      });
  }

  /**
   * Handles the response from the shipping rates API.
   * @param {Object} response - The response data.
   */
  handleRatesResponse(response) {
    if (response.error) {
      this.handleErrorResponse(response);
      return;
    }

    // Success case with shipping rates
    if (response.shipping_rates && response.shipping_rates.length > 0) {
      this.showRates(response.shipping_rates);
    } else {
      // Empty rates but no error - could be MOV issue that didn't get proper error code
      const cartTotalElement = document.querySelector(
        'meta[name="cart-total"]'
      );
      const cartTotal = cartTotalElement
        ? parseFloat(cartTotalElement.content) / 100
        : 0;

      if (cartTotal < 150) {
        // If cart total is below general MOV, show MOV message
        this.showErrorMessage(
          this.errorMessageMapping["MINIMUM_ORDER_NOT_MET"]
        );
      } else {
        // Default case
        this.showErrorMessage(this.errorMessageMapping["NO_SHIPPING_OPTION"]);
      }
    }
  }

  /**
   * Handle specific error responses with appropriate messages
   * @param {Object} response - The error response
   */
  handleErrorResponse(response) {
    // Check if we have inventory issues first
    if (window.cartHasOutOfStockItems) {
      this.showErrorMessage(this.errorMessageMapping["INVENTORY_ISSUES"]);
      return;
    }
  
    // Get cart total to check login + MOV status
    const cartTotalElement = document.querySelector('meta[name="cart-total"]');
    const cartTotal = cartTotalElement ? parseFloat(cartTotalElement.content) : 0;
    const generalMOV = 15000; // £150 in pennies
  
    // For non-logged in users with sufficient total, show login message
    if (!this.isLoggedIn && cartTotal >= generalMOV) {
      this.showErrorMessage(this.errorMessageMapping["LOGIN_REQUIRED"]);
      return;
    }
  
    // Check if we have a specific error code
    if (response.error.code) {
      const errorMessage =
        this.errorMessageMapping[response.error.code] || response.error.message;
      this.showErrorMessage(errorMessage);

      // For MOV errors, add additional helpful information if available
      if (
        response.error.code === "MINIMUM_ORDER_NOT_MET" &&
        response.error.details
      ) {
        const { current_total, minimum_required, amount_needed } =
          response.error.details;
        if (amount_needed) {
          const amountFormatted = this.formatMoney(amount_needed * 100);
          const additionalInfo = document.createElement("p");
          additionalInfo.classList.add("shipping-calculator__mov-info");

          // Add special customer messaging if applicable
          if (window.isSpecialCustomer) {
            additionalInfo.textContent = `As a special customer, add ${amountFormatted} more to your cart to qualify for shipping.`;
          } else {
            additionalInfo.textContent = `Add ${amountFormatted} more to your cart to qualify for shipping.`;
          }

          // Add the additional info after clearing the main message
          this.responseElement.appendChild(additionalInfo);
        }
      }
    } else {
      // Fallback to generic message or provided message
      this.showErrorMessage(
        response.error.message ||
          "An error occurred with the shipping calculation."
      );
    }
  }

  /**
   * Formats money amount according to shop's money format.
   * @param {number} cents - Amount in cents.
   * @returns {string}
   */
  formatMoney(cents) {
    return Shopify.formatMoney(cents, theme.moneyFormat);
  }

  /**
   * Shows list of shipping rates.
   * @param {Array} shippingRates - List of calculated shipping rates.
   */
  showRates(shippingRates) {
    this.responseElement.innerHTML = `
      <p class="shipping-calculator__message">${
        theme.strings.shippingCalcRatesAvailable
      }</p>
      <ul role="list" class="shipping-calculator__list">
        ${shippingRates
          .map(
            (rate) => `
          <li class="shipping-calculator__rate">
            ${rate.name}: ${this.formatMoney(rate.price)}
          </li>`
          )
          .join("")}
      </ul>
    `;
    this.responseElement.hidden = false;
  }

  /**
   * Shows an error message.
   * @param {string} message - Error message to show.
   */
  showErrorMessage(message) {
    this.responseElement.innerHTML = `<p class="shipping-calculator__message--error">${message}</p>`;
    this.responseElement.hidden = false;
  }

  /**
   * Hides the error message element.
   */
  hideErrorMessage() {
    this.responseElement.innerHTML = "";
    this.responseElement.hidden = true;
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const calculators = document.querySelectorAll(".shipping-calculator");

  if (calculators.length > 0) {
    calculators.forEach((calculator) => {
      new ShippingCalculator(calculator);
    });
  }
});
